import { ContactDetailsFormValues } from '../types/Contact';

/**
 * Maps a raw `/api/contact` response row to the FE's
 * `ContactDetailsFormValues` shape. Extracted from `ContactTable.tsx`
 * which used to inline this twice (initial load + load-more) and the two
 * copies had drifted (the load-more path was missing the
 * `k.postalCode` / `k.city` / `k.street` top-level fallbacks).
 *
 * This is the strictly-more-inclusive variant (the initial-load path).
 */
export function mapContactRow(k: any): ContactDetailsFormValues {
  return {
    kontaktId: k.id,
    tenantId: k.tenantId ?? 'default',
    displayName: k.displayName,
    plz: k.defaultAddress?.postalCode ?? k.postalCode ?? '',
    ort: k.defaultAddress?.city ?? k.city ?? '',
    strasse: k.defaultAddress?.street ?? k.street ?? '',
    // Das Land war bisher nicht in der Zeile, obwohl das Backend die ganze
    // Standardanschrift mitliefert. Ohne es zeigte der Maps-Link für
    // „Kerkstraat 12, 1234 AB Amsterdam" auf einen gleichnamigen deutschen Ort.
    land: k.defaultAddress?.country ?? k.country ?? '',
    typ: k.type?.type ?? '',
    // Verstorben: Die Zeile führt das Kreuz vor dem Namen. Beide Angaben
    // kommen mit — `istVerstorben` nimmt das Flag als führend und fällt auf
    // das Datum zurück (utils/contactDisplay.ts).
    deceased: k.deceased ?? undefined,
    deceasedAt: k.deceasedAt ?? undefined,
    defaultPhone: k.defaultPhone,
    defaultBankAccount: k.defaultBankAccount,
    // legalForm can be object or string; pull the .legalForm/.name out of
    // the object, fall back to the string form, then to the
    // gesellschaftsform mirror field.
    legalForm:
      (k.legalForm &&
        typeof k.legalForm === 'object' &&
        (k.legalForm.legalForm ?? k.legalForm.name)) ||
      (typeof k.legalForm === 'string' ? k.legalForm : undefined) ||
      k.gesellschaftsform ||
      '',
    status: k.status,
    kontaktStatus: k.status?.status ?? '',
    // „CO intern": Der Server liefert das Feld nur an Berechtigte; sonst kommt
    // es als null, auch bei einem Kontakt, der eine CO-Art trägt. Die Spalte
    // „CO-Art" existiert ohnehin nur in der Liste „CO intern".
    coArten: k.coArten ?? null,
    // Die Angaben der Lenkungsliste. Nur die Liste „CO intern" fordert sie an
    // (`?include=cointern`); überall sonst ist das Feld schlicht nicht dabei.
    coIntern: k.coIntern ?? null,
    roles: Array.isArray(k.roles) ? k.roles : [],
    kontaktRole:
      k.roles && k.roles.length > 0
        ? k.roles[0]?.roleDefinition?.contactRole ?? ''
        : '',
    kundenberater: k.boUser?.displayName
      ? `${k.boUser.displayName}${k.boUser.kuerzel ? ` (${k.boUser.kuerzel})` : ''}`
      : k.accountManagerBO
        ? `${k.accountManagerBO.lastname ?? ''}, ${k.accountManagerBO.firstName ?? ''}`
        : 'Nicht zugewiesen',
    foKundenberater: k.foUser?.displayName
      ? `${k.foUser.displayName}${k.foUser.kuerzel ? ` (${k.foUser.kuerzel})` : ''}`
      : k.accountManagerFO
        ? `${k.accountManagerFO.lastname ?? ''}, ${k.accountManagerFO.firstName ?? ''}`
        : 'Nicht zugewiesen',
    accountManagerBO: k.accountManagerBO,
    accountManagerFO: k.accountManagerFO,
    foUser: k.foUser,
    foUsers: Array.isArray(k.foUsers) ? k.foUsers : undefined,
    boUser: k.boUser,
    boTeam: k.boTeam,
    open_tasks: [],
    group: k.group,
    erreichbarkeit: k.erreichbarkeit,
    relevanz: k.contactRelevance,
    photo_url: k.photo_url,
    phase: k.phase,
    value: k.contactValue ? Number(k.contactValue).toLocaleString('de-DE') : '',
    // Server-computed annotation — open-Vorgang indicator ('V').
    openEventTypes: Array.isArray(k.openEventTypes) ? k.openEventTypes : [],
    // Server-computed — id eines offenen NKG-Vorgangs (Klick → /vorgaenge/:id).
    nkgVorgangId: typeof k.nkgVorgangId === 'number' ? k.nkgVorgangId : null,
    birthDate: typeof k.birthDate === 'string' ? k.birthDate : undefined,
    neueMail:
      k.neueMail && typeof k.neueMail.zeit === 'string'
        ? { zeit: k.neueMail.zeit, betreff: String(k.neueMail.betreff ?? '') }
        : null,
    verpassterAnruf:
      k.verpassterAnruf && typeof k.verpassterAnruf.callId === 'number'
        ? {
            callId: k.verpassterAnruf.callId,
            callIds: Array.isArray(k.verpassterAnruf.callIds)
              ? k.verpassterAnruf.callIds.filter((id: unknown) => typeof id === 'number')
              : [k.verpassterAnruf.callId],
            zeit: String(k.verpassterAnruf.zeit ?? ''),
            nummer: typeof k.verpassterAnruf.nummer === 'string' ? k.verpassterAnruf.nummer : null,
            anzahl: Number(k.verpassterAnruf.anzahl) || 1,
            team: k.verpassterAnruf.team === true,
          }
        : null,
    phones: Array.isArray(k.communications)
      ? k.communications
          .filter(
            (p: any) => p.type === 'phone' || p.type === 'mobile' || p.type === 'fax',
          )
          .map((p: any, idx: number) => ({
            id: String(p.id ?? idx + 1),
            label: p.label ?? `Tel ${idx + 1}`,
            number: p.value ?? '',
            type: p.type,
            note: p.note,
            primary: !!(p.primary || p.isPrimary || idx === 0),
          }))
      : [],
    emails: Array.isArray(k.communications)
      ? k.communications
          .filter((p: any) => p.type === 'email')
          .map((p: any, idx: number) => ({
            id: String(p.id ?? idx + 1),
            label: p.label ?? `Email ${idx + 1}`,
            address: p.value ?? '',
            type: p.type,
            note: p.note,
          }))
      : [],
    socialMedia: Array.isArray(k.communications)
      ? k.communications
          .filter(
            (p: any) =>
              ['linkedin', 'instagram', 'facebook', 'tiktok', 'xing', 'youtube', 'homepage'].includes(
                p.type,
              ) &&
              // Leere/entfernte Einträge nicht als Social-Link anzeigen.
              (p.value ?? '').trim() !== '',
          )
          .map((p: any, idx: number) => ({
            id: String(p.id ?? idx + 1),
            label: p.label ?? p.type,
            value: p.value ?? '',
            type: p.type,
            note: p.note,
          }))
      : [],
    relatedCommunications: Array.isArray(k.relationsSrc)
      ? k.relationsSrc.flatMap((rel: any) => {
          const dest = rel.contactDest;
          const role = rel.relationType?.contactRelationType ?? 'Unbekannt';
          if (!dest || !Array.isArray(dest.communications)) return [];
          return dest.communications.map((pc: any) => ({
            contactId: String(dest.id),
            name:
              (dest.lastname || '') +
              (dest.firstName ? ', ' + dest.firstName : ''),
            role,
            id: String(pc.id),
            type: pc.type,
            label: pc.label,
            value: pc.value,
            note: pc.note,
            erreichbarkeit: dest.erreichbarkeit,
          }));
        })
      : [],
    relationPhones: Array.isArray(k.relationsSrc)
      ? k.relationsSrc.flatMap((rel: any) => {
          const dest = rel.contactDest;
          const role = rel.relationType?.contactRelationType ?? 'Unbekannt';
          if (!dest || !Array.isArray(dest.communications)) return [];
          return dest.communications
            .filter(
              (pc: any) =>
                pc.type === 'phone' || pc.type === 'mobile' || pc.type === 'fax',
            )
            .map((pc: any) => ({
              contactId: String(dest.id),
              name:
                (dest.lastname || '') +
                (dest.firstName ? ', ' + dest.firstName : ''),
              role,
              func: rel.function ?? null,
              isExtern: !!rel.isExtern,
              // Typ des Beziehungsziels (Person/Firma) — nötig, um echte
              // Ansprechpartner (Person) von Firma→Firma-Kaskadenkanten
              // (Tochter/Schwester) zu unterscheiden.
              destTyp: dest.type?.type ?? '',
              id: String(pc.id),
              type: pc.type,
              label: pc.label,
              number: pc.value,
              note: pc.note,
            }));
        })
      : [],
    relationEmails: Array.isArray(k.relationsSrc)
      ? k.relationsSrc.flatMap((rel: any) => {
          const dest = rel.contactDest;
          const role = rel.relationType?.contactRelationType ?? 'Unbekannt';
          if (!dest || !Array.isArray(dest.communications)) return [];
          return dest.communications
            .filter((pc: any) => pc.type === 'email')
            .map((pc: any) => ({
              contactId: String(dest.id),
              name:
                (dest.lastname || '') +
                (dest.firstName ? ', ' + dest.firstName : ''),
              role,
              func: rel.function ?? null,
              isExtern: !!rel.isExtern,
              // Typ des Beziehungsziels (Person/Firma) — s. relationPhones.
              destTyp: dest.type?.type ?? '',
              id: String(pc.id),
              type: pc.type,
              label: pc.label,
              address: pc.value,
              note: pc.note,
            }));
        })
      : [],
  } as ContactDetailsFormValues;
}
